'use client';

import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import 'react-phone-number-input/style.css';
import { useGetUserInfoQuery, useUpdateUserInfoMutation } from '@/src/api/api';
import { useSearchParams } from 'next/navigation';
import FieldsRepeater from '@components/Form/FieldsRepeater';
import LoadingBlock from '@components/loadingSkeletons/loadingBlock';
import ModalTitle from '@components/ModalTitle';
import { useEffect, useState } from 'react';
import Confirmation from '@components/Confirmation';
import { BiSolidUserMinus, BiSolidUserPlus } from 'react-icons/bi';
import ErrorMsg from '@components/ErrorMsg';
import getOption from '@/src/utils/getOption';

export default function InfoConfirmationForm() {
  const t = useTranslations('InfoConfirmationForm');
  const l = useTranslations('RegistrationForm.label');
  const c = useTranslations('CompanyRegistrationForm');
  const g = useTranslations('general');
  const f = useTranslations('generalForms');
  const e = useTranslations('errors');
  const searchParams = useSearchParams();
  const key = searchParams.get('customer_key');
  const {
    data,
    isFetching,
    isError,
    error: requestErrors,
  } = useGetUserInfoQuery(key);
  const [updateInfo, { isSuccess }] = useUpdateUserInfoMutation();
  const requestError = requestErrors as any;
  const error = requestError?.data;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = useForm();

  const validate = () => {
    setValue('contacts', contacts);
    const values = getValues();
    values.key = key;
    values.country = values.country
      ? String(values.country).toLowerCase()
      : null;
    values.zipcode = values.zipcode ? String(values.zipcode) : null;
    updateInfo(values);
  };

  const [, triggerUpdate] = useState({});

  const contacts = getValues('contacts') || [];

  useEffect(() => {
    if (data?.data?.contacts?.length) {
      setValue('contacts', [...data?.data?.contacts]);
      triggerUpdate({});
    }
  }, [data, setValue]);
  const inputClasses = 'text-black w-full block py-1 px-2';
  return (
    <div className='w-full'>
      <ModalTitle title={isSuccess ? t('titleSuccess') : t('title')} />
      <p className='mb-2'>
        {isSuccess
          ? t.rich('successUpdateDescription')
          : t.rich('updateDescription', {
              span: (chunks) => (
                <span className='text-brand-alt'>{chunks}</span>
              ),
              br: () => <br/>,
            })}
      </p>
      {isError && <>{e('desc')}</>}
      {isFetching && <LoadingBlock />}
      {!isFetching && !isSuccess && data?.data && (
        <form method='post' onSubmit={handleSubmit(validate)}>
          <FieldsRepeater
            errors={errors}
            error={error?.errors}
            register={register}
            t={l}
            setValue={setValue}
            trigger={trigger}
            inputClasses={inputClasses}
            data={data?.data}
            fields={[{ name: 'name', required: true, label: f('companyName') }]}
          />
          <h2 className='mt-4 font-bold text-brand'>{l('billingAddress')}</h2>
          <FieldsRepeater
            errors={errors}
            error={error?.errors}
            register={register}
            t={l}
            setValue={setValue}
            trigger={trigger}
            inputClasses={inputClasses}
            data={data?.data}
            fields={[
              { name: 'address', required: true },
              { name: 'zipcode', required: true, label: g('area.zip') },
              { name: 'city', required: true },
              { name: 'country', required: true, type: 'country' },
            ]}
          />
          <h2 className='mt-4 font-bold text-brand'>{l('postAddress')}</h2>
          <FieldsRepeater
            errors={errors}
            error={error?.errors}
            register={register}
            t={l}
            setValue={setValue}
            trigger={trigger}
            inputClasses={inputClasses}
            data={data?.data}
            fields={[
              { name: 'post_address', label: l('address') },
              { name: 'post_zipcode', label: g('area.zip') },
              { name: 'post_city', label: l('city') },
            ]}
          />
          <div className='pt-6'></div>
          <FieldsRepeater
            errors={errors}
            error={error?.errors}
            register={register}
            t={l}
            setValue={setValue}
            trigger={trigger}
            inputClasses={inputClasses}
            data={data?.data}
            fields={[
              { name: 'phone', required: true, label: g('phone') },
              { name: 'email', required: true },
            ]}
          />
          <div className='pt-6'></div>
          <FieldsRepeater
            errors={errors}
            error={error?.errors}
            register={register}
            t={l}
            setValue={setValue}
            trigger={trigger}
            inputClasses={inputClasses}
            data={data?.data}
            fields={
                [
                    {
                        name: 'orgno',
                        label: c('number'),
                        required: data?.data?.is_company,
                        skip: !data?.data?.is_company,
                    }
                ]
            }
          />
          {getOption('COLLECT_BANKACCOUNT_INFO') && (
            <FieldsRepeater
              errors={errors}
              error={error?.errors}
              register={register}
              t={l}
              setValue={setValue}
              trigger={trigger}
              inputClasses={inputClasses}
              data={data?.data}
              fields={
                    [
                        {
                            name: 'account_number',
                            required: true,
                            label: data?.data.is_company && getOption('DEFAULT_LOCALE') === 'sv' ? t('bankgiroNr') : g('accountNumber'),
                        }
                    ]
                }
            />
          )}
          <div className='pt-6'></div>
          {contacts
            .filter((contact: any) => !contact.delete)
            .map((contact: any, id: number) => (
              <div key={id} className='my-2'>
                <h2 className='font-bold text-brand'>
                  {t('contactPerson')} #{id + 1}
                </h2>
                <Confirmation
                  onConfirm={() => {
                    interface Contact {
                      id: number;
                      name: string;
                      email: string;
                      cellphone: string;
                      delete?: boolean;
                    }
                    const updatedContacts = contacts.map(
                      (c: Contact, index: number) =>
                        index === id ? { ...c, delete: true } : c
                    );

                    const filteredContacts = updatedContacts.filter(
                      (c: Contact) => !c.delete
                    );

                    setValue('contacts', filteredContacts);
                    triggerUpdate({});
                  }}
                  title={g('remove')}
                  className='mb-3 mt-2 rounded-md bg-[#E11] p-2 text-xl text-black hover:bg-white focus:bg-white'
                >
                  <BiSolidUserMinus />
                </Confirmation>
                {/* Felter for navn, e-post, telefon, etc. */}
                <label className='flex flex-col gap-1'>
                  {g('name')}
                  <input
                    className='mb-2 p-2 text-black'
                    {...register(`contacts[${id}].name`, {
                      value: contact.name,
                      required: {
                        value: true,
                        message: g('required'),
                      },
                    })}
                  />
                  <ErrorMsg
                    errors={errors}
                    error={error?.errors}
                    name={`contacts[${id}].name`}
                  />
                </label>
                <label className='flex flex-col gap-1'>
                  {g('email')}
                  <input
                    className='mb-2 p-2 text-black'
                    type='email'
                    {...register(`contacts[${id}].email`, {
                      value: contact.email,
                      required: {
                        value: true,
                        message: g('required'),
                      },
                    })}
                  />
                  <ErrorMsg
                    errors={errors}
                    error={error?.errors}
                    name={`contacts[${id}].email`}
                  />
                </label>
                <label className='flex flex-col gap-1'>
                  {g('phone')}
                  <input
                    className='mb-2 p-2 text-black'
                    {...register(`contacts[${id}].cellphone`, {
                      value: contact.cellphone,
                      required: {
                        value: true,
                        message: g('required'),
                      },
                    })}
                  />
                  <ErrorMsg
                    errors={errors}
                    error={error?.errors}
                    name={`contacts[${id}].cellphone`}
                  />
                </label>
                <input
                  type='hidden'
                  {...register(`contacts[${id}].id`, {
                    value: contact.id || null,
                  })}
                />
              </div>
            ))}

          <button
            onClick={() => {
              const scopeContacts = [...contacts];
              scopeContacts.push({});
              setValue('contacts', scopeContacts);
              triggerUpdate({});
            }}
            className='mb-3 mt-2 flex items-center gap-1 rounded-md border border-positive bg-positive p-2 text-black text-white hover:bg-black focus:bg-black'
          >
            <BiSolidUserPlus /> {t('addContact')}
          </button>
          <button
            type='submit'
            className='mt-4 mt-6 block rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
          >
            {g('confirm')}
          </button>
        </form>
      )}
    </div>
  );
}
